import type { MobiLauncherConfig } from '@schema/index.js';
import type { Tile } from '@schema/tiles.js';

/**
 * Device facts the launcher can't determine on its own, fetched from the
 * licensing server's `pilot-context` endpoint. Only `tags` need this now —
 * subnet rules match the device's OWN LAN IPv4 (from its identity), so they
 * resolve client-side without a fetch. A missing value leaves tag rules
 * failing closed (the tile stays hidden).
 */
export interface FetchedPilotContext {
  tags?: readonly string[];
}

/**
 * Does any tile in the config use a `tag` pilot rule — the only kind that
 * still needs server-supplied context? Percentage, ramp, AND subnet all
 * resolve from data the device already has, so when this is false the
 * launcher skips the network call entirely.
 */
export function configNeedsPilotContext(config: MobiLauncherConfig): boolean {
  const needs = (tile: Tile): boolean =>
    ('pilot' in tile ? tile.pilot?.rule.kind : undefined) === 'tag';
  for (const profile of config.profiles) {
    for (const list of [profile.grid.tiles, profile.tools, profile.featuredTiles]) {
      for (const tile of list) {
        if (needs(tile)) return true;
        if (tile.type === 'folder' && tile.children.some(needs)) return true;
      }
    }
  }
  return false;
}

/**
 * Fetch this device's pilot context. Best-effort: any failure resolves to
 * `null`, leaving subnet/tag pilots failing closed until a later poll
 * succeeds — never blocks or breaks the launcher.
 */
export async function fetchPilotContext(
  apiBaseUrl: string,
  deviceId: string,
  tenantId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<FetchedPilotContext | null> {
  try {
    const base = apiBaseUrl.replace(/\/$/, '');
    // tenantId scopes the lookup server-side: tags are returned only when it
    // matches the device's tenant, so a leaked device id alone can't reveal
    // another customer's org labels.
    const res = await fetchImpl(
      `${base}/devices/${encodeURIComponent(deviceId)}/pilot-context?tenantId=${encodeURIComponent(tenantId)}`,
      { cache: 'no-store' },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as { tags?: unknown };
    const out: FetchedPilotContext = {};
    if (Array.isArray(json.tags)) {
      out.tags = json.tags.filter((x): x is string => typeof x === 'string');
    }
    return out;
  } catch {
    return null;
  }
}
